import { useState } from 'react'
import * as React from 'react'
import { useNavigate } from 'react-router-dom'
import {
  DialogBackdrop,
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogPositioner,
  DialogRoot,
  DialogTitle,
  Button,
  Input,
  VStack,
  Textarea,
  Box,
  Text,
  HStack,
  IconButton,
  Badge,
  RadioGroup,
  Toaster,
  createToaster,
  Steps,
} from '@chakra-ui/react'
import { NativeSelectRoot, NativeSelectField } from '@chakra-ui/react'
import { Field } from '@chakra-ui/react'
import { useTranslation } from 'react-i18next'
import { ChevronDown, X, Plus, Sparkles, Home, User, Building2, Calculator, Check, AlertCircle } from 'lucide-react'
import { useForm, useFieldArray, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useCreateOwnerWithNewUnits } from '../hooks/useOwners'
import { useUnits } from '@/features/units/hooks/useUnits'
import { NewUnitInlineForm } from './NewUnitInlineForm'
import { UnitSelector } from './UnitSelector'
import { OwnershipDistributor } from './OwnershipDistributor'
import { FractionInput } from '@/shared/components/FractionInput'
import { supabase } from '@/lib/supabase'

const toaster = createToaster({
  placement: 'top-end',
  pauseOnPageIdle: true,
})

// Validation schema with at least one unit requirement
const ownerCreateSchema = z.object({
  firstName: z.string().min(1, 'Le prénom est requis'),
  lastName: z.string().min(1, 'Le nom est requis'),
  email: z.string().email('Email invalide').optional().or(z.literal('')),
  phone: z.string().optional(),
  address: z.string().optional(),
  preferredContact: z.enum(['EMAIL', 'PAPER']),
  existingUnits: z
    .array(
      z.object({
        unit_id: z.string().min(1, 'Le lot est requis'),
        start_date: z.string().min(1, 'La date de début est requise'),
        share_numerator: z.number().min(1).optional(),
        share_denominator: z.number().min(1).optional(),
      })
    )
    .optional(),
  newUnits: z
    .array(
      z.object({
        residence_id: z.string().min(1, 'La résidence est requise'),
        unit_number: z.string().min(1, 'Le numéro de lot est requis'),
        unit_type: z.enum(['RESIDENTIAL', 'PARKING', 'CELLAR', 'COMMERCIAL']),
        floor: z.string().optional(),
        stairwell: z.string().optional(),
        area_sqm: z.string().optional(),
        start_date: z.string().min(1, 'La date de début est requise'),
        share_numerator: z.number().min(1).optional(),
        share_denominator: z.number().min(1).optional(),
      })
    )
    .optional(),
}).refine(
  (data) => {
    const hasExistingUnits = data.existingUnits && data.existingUnits.length > 0
    const hasNewUnits = data.newUnits && data.newUnits.length > 0
    return hasExistingUnits || hasNewUnits
  },
  {
    message: 'Au moins un lot (existant ou nouveau) doit être associé au copropriétaire',
    path: ['existingUnits'],
  }
)

type OwnerCreateFormData = z.infer<typeof ownerCreateSchema>

interface OwnerCreateModalProps {
  isOpen: boolean
  onClose: () => void
  onSuccess?: (ownerId: string) => void
}

interface ExistingOwnership {
  ownership_id: string
  owner_id: string
  owner_first_name: string
  owner_last_name: string
  ownership_share: number
  share_numerator: number
  share_denominator: number
  start_date: string
}

interface UnitOwnershipMap {
  [unitId: string]: {
    unitNumber: string
    existingOwners: ExistingOwnership[]
    distributionShares: Array<{
      ownerId: string
      ownerName: string
      ownershipId?: string
      share: number
      shareNumerator: number
      shareDenominator: number
      isNew?: boolean
    }>
  }
}

export function OwnerCreateModal({ isOpen, onClose, onSuccess }: OwnerCreateModalProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const createOwner = useCreateOwnerWithNewUnits()
  const { data: allUnits = [] } = useUnits()
  const [unitCreationMode, setUnitCreationMode] = useState<'existing' | 'new'>('existing')
  const [hasInitialized, setHasInitialized] = useState(false)
  const [currentStep, setCurrentStep] = useState(0)
  const [stepValidation, setStepValidation] = useState<{
    [key: number]: 'valid' | 'invalid' | 'pending'
  }>({
    0: 'pending',
    1: 'pending',
    2: 'pending',
  })

  // Track existing ownerships for each selected unit
  const [unitOwnershipsMap, setUnitOwnershipsMap] = useState<UnitOwnershipMap>({})

  const steps = [
    { title: 'Identité', icon: User },
    { title: 'Lots', icon: Building2 },
    { title: 'Répartitions', icon: Calculator },
  ]

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    reset,
    watch,
    trigger,
    setValue,
  } = useForm<OwnerCreateFormData>({
    resolver: zodResolver(ownerCreateSchema),
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      address: '',
      preferredContact: 'EMAIL',
      existingUnits: [],
      newUnits: [],
    },
  })

  const {
    fields: existingUnitsFields,
    append: appendExistingUnit,
    remove: removeExistingUnit,
  } = useFieldArray({
    control,
    name: 'existingUnits',
  })

  const {
    fields: newUnitsFields,
    append: appendNewUnit,
    remove: removeNewUnit,
  } = useFieldArray({
    control,
    name: 'newUnits',
  })

  // Watch units to show count
  const existingUnits = watch('existingUnits')
  const newUnits = watch('newUnits')
  const totalUnits = (existingUnits?.length || 0) + (newUnits?.length || 0)
  const firstName = watch('firstName')
  const lastName = watch('lastName')

  // Function to check existing ownerships for a unit
  const checkUnitOwnership = async (unitId: string) => {
    if (!unitId) return

    try {
      const { data, error } = await supabase.rpc('check_unit_ownerships' as any, {
        p_unit_id: unitId,
      })

      if (error) {
        console.error('Error checking unit ownership:', error)
        return
      }

      if (data && Array.isArray(data) && data.length > 0) {
        // Unit has existing ownerships
        const selectedUnit = allUnits.find((u) => u.id === unitId)
        const unitNumber = selectedUnit?.unit_number || '?'

        const existingOwners: ExistingOwnership[] = data.map((row: any) => ({
          ownership_id: row.ownership_id,
          owner_id: row.owner_id,
          owner_first_name: row.owner_first_name,
          owner_last_name: row.owner_last_name,
          ownership_share: row.ownership_share,
          share_numerator: row.share_numerator,
          share_denominator: row.share_denominator,
          start_date: row.start_date,
        }))

        setUnitOwnershipsMap((prev) => ({
          ...prev,
          [unitId]: {
            unitNumber,
            existingOwners,
            distributionShares: [],
          },
        }))
      } else {
        // No existing ownerships, remove from map if present
        setUnitOwnershipsMap((prev) => {
          const newMap = { ...prev }
          delete newMap[unitId]
          return newMap
        })
      }
    } catch (err) {
      console.error('Error checking unit ownership:', err)
    }
  }

  // Auto-add first form when modal opens
  React.useEffect(() => {
    if (isOpen && !hasInitialized && existingUnitsFields.length === 0) {
      const today = new Date().toISOString().split('T')[0] as string
      appendExistingUnit({
        unit_id: '',
        start_date: today,
        share_numerator: 1,
        share_denominator: 1,
      })
      setHasInitialized(true)
    }
  }, [isOpen, hasInitialized, existingUnitsFields.length, appendExistingUnit])

  const handleClose = () => {
    reset()
    setUnitCreationMode('existing')
    setHasInitialized(false)
    setCurrentStep(0)
    setStepValidation({ 0: 'pending', 1: 'pending', 2: 'pending' })
    setUnitOwnershipsMap({}) // Clear ownership distributions
    onClose()
  }

  const validateCurrentStep = async (): Promise<boolean> => {
    if (currentStep === 0) {
      // Validate step 1: Personal info
      const result = await trigger(['firstName', 'lastName', 'email', 'phone', 'address', 'preferredContact'])
      setStepValidation((prev) => ({ ...prev, 0: result ? 'valid' : 'invalid' }))
      return result
    } else if (currentStep === 1) {
      // Validate step 2: Units association
      const hasUnits = totalUnits > 0
      setStepValidation((prev) => ({ ...prev, 1: hasUnits ? 'valid' : 'invalid' }))
      return hasUnits
    }
    return true
  }

  const handleNextStep = async () => {
    const isValid = await validateCurrentStep()
    if (isValid && currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1)
    }
  }

  const handlePreviousStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1)
    }
  }

  const handleSkipStep = async () => {
    // Skip step 3 and submit directly
    const data = watch()
    await onSubmit(data)
  }

  const onSubmit = async (data: OwnerCreateFormData) => {
    try {
      // Check if any unit has distribution (existing owners)
      const hasDistributions = Object.keys(unitOwnershipsMap).length > 0

      if (hasDistributions) {
        // Use the distribution-aware RPC
        const unitDistributions: { [unitId: string]: any[] } = {}

        // Build distribution map
        Object.entries(unitOwnershipsMap).forEach(([unitId, unitInfo]) => {
          if (unitInfo.distributionShares && unitInfo.distributionShares.length > 0) {
            unitDistributions[unitId] = unitInfo.distributionShares
          }
        })

        const { data: result, error } = await supabase.rpc('create_owner_with_distribution' as any, {
          p_first_name: data.firstName,
          p_last_name: data.lastName.toUpperCase(),
          p_email: data.email || undefined,
          p_phone: data.phone || undefined,
          p_address: data.address || undefined,
          p_preferred_contact: data.preferredContact,
          p_existing_units: data.existingUnits && data.existingUnits.length > 0 ? data.existingUnits : [],
          p_new_units: data.newUnits && data.newUnits.length > 0
            ? data.newUnits.map((unit) => ({
                ...unit,
                area_sqm: unit.area_sqm ? Number(unit.area_sqm) : undefined,
              }))
            : [],
          p_unit_distributions: unitDistributions,
        })

        if (error) throw error

        const typedResult = result as any

        handleClose()

        // Show success toast
        toaster.create({
          title: 'Copropriétaire créé avec succès',
          description: typedResult.has_created_new_units
            ? `${typedResult.new_unit_ids.length} nouveau(x) lot(s) créé(s). Les parts ont été réparties.`
            : 'Le copropriétaire a été créé et les parts ont été réparties.',
          type: 'success',
          duration: 5000,
        })

        // Smart redirection
        if (typedResult.has_created_new_units && typedResult.new_unit_ids.length > 0) {
          setTimeout(() => {
            navigate('/app/units', {
              state: { openDrawerId: typedResult.new_unit_ids[0] },
            })
          }, 500)
        } else if (onSuccess) {
          onSuccess(typedResult.owner_id)
        }
      } else {
        // No distributions - use the standard RPC
        const payload = {
          firstName: data.firstName,
          lastName: data.lastName.toUpperCase(),
          email: data.email || undefined,
          phone: data.phone || undefined,
          address: data.address || undefined,
          preferredContact: data.preferredContact,
          existingUnits: data.existingUnits && data.existingUnits.length > 0 ? data.existingUnits : undefined,
          newUnits: data.newUnits && data.newUnits.length > 0
            ? data.newUnits.map((unit) => ({
                ...unit,
                area_sqm: unit.area_sqm ? Number(unit.area_sqm) : undefined,
              }))
            : undefined,
        }

        const result = await createOwner.mutateAsync(payload)

        handleClose()

        // Show success toast
        toaster.create({
          title: 'Copropriétaire créé avec succès',
          description: result.has_created_new_units
            ? `${result.new_unit_ids.length} nouveau(x) lot(s) créé(s). Complétez les détails.`
            : 'Le copropriétaire a été associé aux lots existants.',
          type: 'success',
          duration: 5000,
        })

        // Smart redirection
        if (result.has_created_new_units && result.new_unit_ids.length > 0) {
          // Redirect to units page with first new unit drawer open
          setTimeout(() => {
            navigate('/app/units', {
              state: { openDrawerId: result.new_unit_ids[0] },
            })
          }, 500)
        } else if (onSuccess) {
          // Stay on owners page and open drawer
          onSuccess(result.owner_id)
        }
      }
    } catch (error: any) {
      console.error('Error creating owner:', error)
      toaster.create({
        title: 'Erreur lors de la création',
        description: error.message || t('common.error'),
        type: 'error',
        duration: 5000,
      })
    }
  }

  const handleAddExistingUnit = () => {
    const today = new Date().toISOString().split('T')[0] as string
    appendExistingUnit({
      unit_id: '',
      start_date: today,
      share_numerator: 1,
      share_denominator: 1,
    })
  }

  const handleAddNewUnit = () => {
    const today = new Date().toISOString().split('T')[0] as string
    appendNewUnit({
      residence_id: '',
      unit_number: '',
      unit_type: 'RESIDENTIAL',
      floor: '',
      stairwell: '',
      area_sqm: '',
      start_date: today,
      share_numerator: 1,
      share_denominator: 1,
    })
  }

  return (
    <>
      <Toaster toaster={toaster}>
        {() => null}
      </Toaster>
      <DialogRoot open={isOpen} onOpenChange={(e) => !e.open && handleClose()} size="xl">
        <DialogBackdrop />
        <DialogPositioner
          display="flex"
          alignItems="center"
          justifyContent="center"
          position="fixed"
          inset="0"
        >
          <DialogContent margin="auto" maxWidth="800px" maxHeight="90vh" width="full">
            <DialogHeader borderBottomWidth="1px">
              <DialogTitle>{t('owners.create')}</DialogTitle>
            </DialogHeader>
            <DialogCloseTrigger />
            <DialogBody pb={6} overflowY="auto" maxHeight="calc(90vh - 240px)">
              {/* Stepper en haut du contenu */}
              <Box mb={6}>
                <Steps.Root
                  step={currentStep}
                  count={steps.length}
                  colorPalette="brand"
                >
                  <Steps.List>
                    {steps.map((step, index) => {
                      const StepIcon = step.icon
                      const validation = stepValidation[index]
                      const isCurrent = index === currentStep

                      // Determine which icon to show
                      let displayIcon = <StepIcon size={16} />
                      if (validation === 'valid' && !isCurrent) {
                        displayIcon = <Check size={16} color="green" />
                      } else if (validation === 'invalid') {
                        displayIcon = <AlertCircle size={16} color="red" />
                      }

                      return (
                        <Steps.Item key={index} index={index}>
                          <Steps.Trigger
                            disabled
                            cursor="default"
                            _hover={{ bg: 'transparent' }}
                          >
                            <Steps.Indicator
                              borderColor={
                                validation === 'valid' ? 'green.500' :
                                validation === 'invalid' ? 'red.500' :
                                isCurrent ? 'brand.500' : 'gray.300'
                              }
                              bg={
                                validation === 'valid' ? 'green.50' :
                                validation === 'invalid' ? 'red.50' :
                                isCurrent ? 'brand.50' : 'white'
                              }
                            >
                              {displayIcon}
                            </Steps.Indicator>
                            <Steps.Title
                              ml={2}
                              color={
                                validation === 'valid' ? 'green.700' :
                                validation === 'invalid' ? 'red.700' :
                                isCurrent ? 'brand.700' : 'gray.600'
                              }
                            >
                              {step.title}
                            </Steps.Title>
                          </Steps.Trigger>
                          {index < steps.length - 1 && <Steps.Separator />}
                        </Steps.Item>
                      )
                    })}
                  </Steps.List>
                </Steps.Root>
              </Box>
              <Box as="form" id="owner-create-form" onSubmit={handleSubmit(onSubmit)}>
                <VStack gap={6} align="stretch">
                  {/* === STEP 1: Informations personnelles === */}
                  {currentStep === 0 && (
                  <Box>
                    <Box borderBottomWidth="1px" pb={3} mb={4}>
                      <Text fontSize="lg" fontWeight="semibold" color="gray.700">
                        Informations personnelles
                      </Text>
                    </Box>
                    <VStack gap={4} align="stretch">
                      <HStack gap={4}>
                        <Field.Root invalid={!!errors.firstName} required flex={1}>
                          <Field.Label>{t('owners.fields.first_name')}</Field.Label>
                          <Input
                            {...register('firstName')}
                            placeholder={t('owners.fields.first_name')}
                          />
                          {errors.firstName && (
                            <Field.ErrorText>{errors.firstName.message}</Field.ErrorText>
                          )}
                        </Field.Root>

                        <Field.Root invalid={!!errors.lastName} required flex={1}>
                          <Field.Label>{t('owners.fields.last_name')}</Field.Label>
                          <Input
                            {...register('lastName')}
                            placeholder={t('owners.fields.last_name')}
                            textTransform="uppercase"
                          />
                          {errors.lastName && (
                            <Field.ErrorText>{errors.lastName.message}</Field.ErrorText>
                          )}
                        </Field.Root>
                      </HStack>

                      <HStack gap={4}>
                        <Field.Root invalid={!!errors.email} flex={1}>
                          <Field.Label>{t('owners.fields.email')}</Field.Label>
                          <Input
                            {...register('email')}
                            type="email"
                            placeholder={t('owners.fields.email')}
                          />
                          {errors.email && (
                            <Field.ErrorText>{errors.email.message}</Field.ErrorText>
                          )}
                        </Field.Root>

                        <Field.Root invalid={!!errors.phone} flex={1}>
                          <Field.Label>{t('owners.fields.phone')}</Field.Label>
                          <Input
                            {...register('phone')}
                            placeholder={t('owners.fields.phone')}
                          />
                          {errors.phone && (
                            <Field.ErrorText>{errors.phone.message}</Field.ErrorText>
                          )}
                        </Field.Root>
                      </HStack>

                      <Field.Root invalid={!!errors.address}>
                        <Field.Label>{t('owners.fields.address')}</Field.Label>
                        <Textarea
                          {...register('address')}
                          placeholder={t('owners.fields.address')}
                          rows={2}
                        />
                        {errors.address && (
                          <Field.ErrorText>{errors.address.message}</Field.ErrorText>
                        )}
                      </Field.Root>

                      <Field.Root invalid={!!errors.preferredContact} required>
                        <Field.Label>{t('owners.fields.preferred_contact')}</Field.Label>
                        <NativeSelectRoot position="relative">
                          <NativeSelectField {...register('preferredContact')} paddingRight="10">
                            <option value="EMAIL">Email</option>
                            <option value="PAPER">Courrier papier</option>
                          </NativeSelectField>
                          <Box
                            position="absolute"
                            right="3"
                            top="50%"
                            transform="translateY(-50%)"
                            pointerEvents="none"
                            color="gray.500"
                          >
                            <ChevronDown size={16} />
                          </Box>
                        </NativeSelectRoot>
                        {errors.preferredContact && (
                          <Field.ErrorText>{errors.preferredContact.message}</Field.ErrorText>
                        )}
                      </Field.Root>
                    </VStack>
                  </Box>
                  )}

                  {/* === STEP 2: Association aux lots (OBLIGATOIRE) === */}
                  {currentStep === 1 && (
                  <Box>
                    <Box borderBottomWidth="1px" pb={3} mb={4}>
                      <HStack gap={2}>
                        <Text fontSize="lg" fontWeight="semibold" color="gray.700">
                          Association aux lots
                        </Text>
                        <Badge colorPalette="brand" size="sm" variant="subtle">
                          Obligatoire
                        </Badge>
                        {totalUnits > 0 && (
                          <Badge colorPalette="green" size="sm">
                            ✓ {totalUnits} lot{totalUnits > 1 ? 's' : ''}
                          </Badge>
                        )}
                      </HStack>
                    </Box>

                    {totalUnits === 0 && (
                      <Box
                        bg="orange.50"
                        borderWidth="1px"
                        borderColor="orange.200"
                        p={3}
                        borderRadius="md"
                        mb={4}
                      >
                        <Text fontSize="sm" color="orange.800" fontWeight="medium">
                          💡 Au moins un lot doit être associé au copropriétaire
                        </Text>
                      </Box>
                    )}

                    {/* Radio Cards pour choisir le mode */}
                    <Text fontSize="sm" fontWeight="medium" color="gray.600" mb={3}>
                      Comment souhaitez-vous ajouter un lot ?
                    </Text>
                    <RadioGroup.Root
                      value={unitCreationMode}
                      onValueChange={(e) => {
                        const newMode = e.value as 'existing' | 'new'
                        setUnitCreationMode(newMode)
                        // Auto-add first form when switching mode
                        const today = new Date().toISOString().split('T')[0] as string
                        if (newMode === 'existing' && existingUnitsFields.length === 0) {
                          appendExistingUnit({
                            unit_id: '',
                            start_date: today,
                            share_numerator: 1,
                            share_denominator: 1,
                          })
                        } else if (newMode === 'new' && newUnitsFields.length === 0) {
                          appendNewUnit({
                            residence_id: '',
                            unit_number: '',
                            unit_type: 'RESIDENTIAL',
                            floor: '',
                            stairwell: '',
                            area_sqm: '',
                            start_date: today,
                            share_numerator: 1,
                            share_denominator: 1,
                          })
                        }
                      }}
                      mb={4}
                    >
                      <HStack gap={3} align="stretch">
                        <RadioGroup.Item
                          value="existing"
                          flex={1}
                          cursor="pointer"
                          p={4}
                          borderWidth="2px"
                          borderRadius="lg"
                          borderColor={unitCreationMode === 'existing' ? 'brand.500' : 'gray.200'}
                          bg={unitCreationMode === 'existing' ? 'brand.50' : 'white'}
                          transition="all 0.2s"
                          _hover={{
                            borderColor: unitCreationMode === 'existing' ? 'brand.600' : 'gray.300',
                            bg: unitCreationMode === 'existing' ? 'brand.100' : 'gray.50',
                          }}
                          position="relative"
                        >
                          <RadioGroup.ItemHiddenInput />
                          <HStack gap={3} align="start">
                            <Box mt={0.5}>
                              <RadioGroup.ItemControl />
                            </Box>
                            <VStack align="start" gap={1} flex={1}>
                              <HStack gap={2}>
                                <Home size={18} />
                                <Text fontWeight="semibold" fontSize="md">
                                  Lots existants
                                </Text>
                                {existingUnitsFields.length > 0 && (
                                  <Badge size="sm" colorPalette="blue">
                                    {existingUnitsFields.length}
                                  </Badge>
                                )}
                              </HStack>
                              <Text fontSize="xs" color="gray.600">
                                Sélectionnez parmi vos lots déjà créés
                              </Text>
                            </VStack>
                          </HStack>
                        </RadioGroup.Item>

                        <RadioGroup.Item
                          value="new"
                          flex={1}
                          cursor="pointer"
                          p={4}
                          borderWidth="2px"
                          borderRadius="lg"
                          borderColor={unitCreationMode === 'new' ? 'brand.500' : 'gray.200'}
                          bg={unitCreationMode === 'new' ? 'brand.50' : 'white'}
                          transition="all 0.2s"
                          _hover={{
                            borderColor: unitCreationMode === 'new' ? 'brand.600' : 'gray.300',
                            bg: unitCreationMode === 'new' ? 'brand.100' : 'gray.50',
                          }}
                          position="relative"
                        >
                          <RadioGroup.ItemHiddenInput />
                          <HStack gap={3} align="start">
                            <Box mt={0.5}>
                              <RadioGroup.ItemControl />
                            </Box>
                            <VStack align="start" gap={1} flex={1}>
                              <HStack gap={2}>
                                <Sparkles size={18} />
                                <Text fontWeight="semibold" fontSize="md">
                                  Nouveau lot
                                </Text>
                                {newUnitsFields.length > 0 && (
                                  <Badge size="sm" colorPalette="brand">
                                    {newUnitsFields.length}
                                  </Badge>
                                )}
                              </HStack>
                              <Text fontSize="xs" color="gray.600">
                                Créez un lot à la volée pour ce copropriétaire
                              </Text>
                            </VStack>
                          </HStack>
                        </RadioGroup.Item>
                      </HStack>
                    </RadioGroup.Root>

                    {/* Content: Lots existants */}
                    {unitCreationMode === 'existing' && (
                        <VStack gap={3} align="stretch" mt={4}>
                          {existingUnitsFields.map((field, index) => (
                            <Box
                              key={field.id}
                              p={3}
                              borderWidth="1px"
                              borderRadius="md"
                              borderColor="gray.200"
                              bg="white"
                            >
                              <HStack justify="space-between" mb={3}>
                                <Text fontSize="sm" fontWeight="semibold">
                                  Lot #{index + 1}
                                </Text>
                                <IconButton
                                  variant="ghost"
                                  size="xs"
                                  colorPalette="red"
                                  onClick={() => removeExistingUnit(index)}
                                  aria-label="Supprimer"
                                >
                                  <X size={14} />
                                </IconButton>
                              </HStack>

                              <VStack gap={2} align="stretch">
                                <Controller
                                  name={`existingUnits.${index}.unit_id`}
                                  control={control}
                                  render={({ field }) => (
                                    <UnitSelector
                                      units={allUnits}
                                      value={field.value ?? ''}
                                      onChange={(unitId) => {
                                        field.onChange(unitId)
                                        checkUnitOwnership(unitId)
                                      }}
                                      error={errors.existingUnits?.[index]?.unit_id?.message}
                                      label="Lot"
                                      placeholder="Rechercher un lot..."
                                    />
                                  )}
                                />

                                {/* Show OwnershipDistributor if unit has existing owners */}
                                {(() => {
                                  const unitId = existingUnits?.[index]?.unit_id
                                  const ownership = unitId ? unitOwnershipsMap[unitId] : undefined

                                  // Show date/share inputs only if unit is selected
                                  if (!unitId) return null

                                  // If unit has existing owners, show distributor
                                  if (ownership) {
                                    return (
                                      <Box mt={2}>
                                        <OwnershipDistributor
                                          unitNumber={ownership.unitNumber}
                                          existingOwners={ownership.existingOwners.map(
                                            (owner) => ({
                                              ownerId: owner.owner_id,
                                              ownerName: `${owner.owner_first_name} ${owner.owner_last_name}`,
                                              ownershipId: owner.ownership_id,
                                              shareNumerator: owner.share_numerator,
                                              shareDenominator: owner.share_denominator,
                                            })
                                          )}
                                          newOwnerName={
                                            firstName && lastName
                                              ? `${firstName} ${lastName}`
                                              : 'Nouveau copropriétaire'
                                          }
                                          startDate={existingUnits?.[index]?.start_date || ''}
                                          onStartDateChange={(date) => {
                                            setValue(`existingUnits.${index}.start_date`, date)
                                          }}
                                          onDistributionChange={(shares) => {
                                            // Store distribution shares for this unit
                                            if (unitId) {
                                              setUnitOwnershipsMap((prev) => {
                                                const existing = prev[unitId]
                                                if (!existing) return prev

                                                return {
                                                  ...prev,
                                                  [unitId]: {
                                                    ...existing,
                                                    distributionShares: shares.map(s => ({
                                                      ...s,
                                                      share: s.shareNumerator / s.shareDenominator,
                                                    })),
                                                  },
                                                }
                                              })
                                            }
                                          }}
                                        />
                                      </Box>
                                    )
                                  }

                                  // Unit selected but no existing owners - show simple fields
                                  return (
                                    <HStack gap={2}>
                                      <Field.Root
                                        invalid={!!errors.existingUnits?.[index]?.start_date}
                                        flex={1}
                                        required
                                      >
                                        <Field.Label fontSize="sm">Date de début</Field.Label>
                                        <Input
                                          {...register(`existingUnits.${index}.start_date`)}
                                          type="date"
                                          size="sm"
                                        />
                                        {errors.existingUnits?.[index]?.start_date && (
                                          <Field.ErrorText>
                                            {errors.existingUnits[index]?.start_date?.message}
                                          </Field.ErrorText>
                                        )}
                                      </Field.Root>

                                      <Field.Root flex={1}>
                                        <Field.Label fontSize="sm" mb={2}>Quote-part</Field.Label>
                                        <Controller
                                          name={`existingUnits.${index}.share_numerator`}
                                          control={control}
                                          render={({ field: numeratorField }) => (
                                            <Controller
                                              name={`existingUnits.${index}.share_denominator`}
                                              control={control}
                                              render={({ field: denominatorField }) => {
                                                const percentage = denominatorField.value
                                                  ? ((numeratorField.value || 1) / denominatorField.value) * 100
                                                  : 100

                                                return (
                                                  <FractionInput
                                                    value={percentage}
                                                    onChange={(numerator, denominator) => {
                                                      numeratorField.onChange(numerator)
                                                      denominatorField.onChange(denominator)
                                                    }}
                                                  />
                                                )
                                              }}
                                            />
                                          )}
                                        />
                                      </Field.Root>
                                    </HStack>
                                  )
                                })()}
                              </VStack>
                            </Box>
                          ))}

                          <Button
                            variant="outline"
                            size="sm"
                            onClick={handleAddExistingUnit}
                            colorPalette="blue"
                          >
                            <Plus size={16} />
                            Ajouter un lot existant
                          </Button>
                        </VStack>
                    )}

                    {/* Content: Nouveaux lots */}
                    {unitCreationMode === 'new' && (
                        <VStack gap={4} align="stretch" mt={4}>
                          {newUnitsFields.map((field, index) => (
                            <NewUnitInlineForm
                              key={field.id}
                              index={index}
                              control={control}
                              errors={errors}
                              onRemove={() => removeNewUnit(index)}
                            />
                          ))}

                          <Button
                            variant="solid"
                            size="sm"
                            onClick={handleAddNewUnit}
                            colorPalette="brand"
                          >
                            <Sparkles size={16} />
                            Créer un nouveau lot
                          </Button>
                        </VStack>
                    )}

                    {errors.existingUnits && 'message' in errors.existingUnits && (
                      <Box
                        mt={3}
                        bg="red.100"
                        borderWidth="1px"
                        borderColor="red.300"
                        p={2}
                        borderRadius="md"
                      >
                        <Text fontSize="sm" color="red.800">
                          {String(errors.existingUnits.message)}
                        </Text>
                      </Box>
                    )}
                  </Box>
                  )}

                  {/* === STEP 3: Clés de répartition (OPTIONNEL - MOCK) === */}
                  {currentStep === 2 && (
                  <Box>
                    <Box borderBottomWidth="1px" pb={3} mb={4}>
                      <HStack gap={2}>
                        <Text fontSize="lg" fontWeight="semibold" color="gray.700">
                          Clés de répartition des charges
                        </Text>
                        <Badge colorPalette="blue" size="sm" variant="subtle">
                          Optionnel
                        </Badge>
                      </HStack>
                    </Box>

                    <VStack align="stretch" gap={4}>

                      <Box
                        bg="blue.50"
                        borderWidth="1px"
                        borderColor="blue.200"
                        p={4}
                        borderRadius="md"
                      >
                        <Text fontSize="sm" color="gray.600" mb={3}>
                          Cette fonctionnalité vous permettra de définir les tantièmes et clés de répartition pour chaque lot associé au copropriétaire.
                        </Text>
                        <Text fontSize="sm" color="gray.600" fontWeight="medium">
                          🚧 Interface en cours de développement
                        </Text>
                        <Text fontSize="sm" color="gray.500" mt={2}>
                          Vous pourrez compléter ces informations ultérieurement depuis la fiche du copropriétaire ou la gestion des lots.
                        </Text>
                      </Box>

                      {/* Mock: Liste des lots à configurer */}
                      {totalUnits > 0 && (
                        <Box
                          bg="white"
                          borderWidth="1px"
                          borderColor="gray.200"
                          p={4}
                          borderRadius="md"
                        >
                          <Text fontSize="sm" fontWeight="semibold" color="gray.700" mb={2}>
                            Lots concernés :
                          </Text>
                          <VStack align="stretch" gap={2}>
                            {existingUnits?.map((_, idx) => (
                              <HStack key={idx} p={2} bg="gray.50" borderRadius="md">
                                <Home size={14} />
                                <Text fontSize="sm">Lot existant #{idx + 1}</Text>
                                <Badge size="sm" colorPalette="gray">À configurer</Badge>
                              </HStack>
                            ))}
                            {newUnits?.map((unit, idx) => (
                              <HStack key={idx} p={2} bg="gray.50" borderRadius="md">
                                <Sparkles size={14} />
                                <Text fontSize="sm">Nouveau lot: {unit.unit_number}</Text>
                                <Badge size="sm" colorPalette="gray">À configurer</Badge>
                              </HStack>
                            ))}
                          </VStack>
                        </Box>
                      )}
                    </VStack>
                  </Box>
                  )}
                </VStack>
              </Box>
            </DialogBody>
            <DialogFooter
              position="sticky"
              bottom={0}
              bg="white"
              borderTopWidth="1px"
              display="flex"
              gap={3}
              justifyContent="space-between"
              p={4}
            >
              {/* Left side: Cancel or Back */}
              <Box>
                {currentStep === 0 ? (
                  <Button variant="outline" onClick={handleClose} disabled={createOwner.isPending}>
                    {t('common.cancel')}
                  </Button>
                ) : (
                  <Button variant="outline" onClick={handlePreviousStep} disabled={createOwner.isPending}>
                    Retour
                  </Button>
                )}
              </Box>

              {/* Right side: Next, Skip, or Submit */}
              <HStack gap={3}>
                {currentStep === 2 && (
                  <Button
                    variant="ghost"
                    onClick={handleSkipStep}
                    disabled={createOwner.isPending}
                    colorPalette="gray"
                  >
                    Passer cette étape
                  </Button>
                )}
                {currentStep < 2 ? (
                  <Button
                    colorPalette="brand"
                    onClick={handleNextStep}
                    disabled={createOwner.isPending}
                  >
                    Suivant
                  </Button>
                ) : (
                  <Button
                    type="submit"
                    form="owner-create-form"
                    colorPalette="brand"
                    loading={createOwner.isPending}
                  >
                    Créer le copropriétaire
                  </Button>
                )}
              </HStack>
            </DialogFooter>
          </DialogContent>
        </DialogPositioner>
      </DialogRoot>
    </>
  )
}
